import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams, useLocation } from 'react-router-dom';
import { PageHeader, SectionCard, StatusPill, EmptyState, InviteTabBar } from '@/components/portal/PortalUI';
import { ClipboardList, CheckCircle2, FileText, FileVideo, ExternalLink, ChevronDown, ChevronUp, AlertCircle, Eye, BookOpen } from 'lucide-react';
import { useInviteToken } from '@shared/utils/inviteToken';
import { getBrief, parseRpcError } from '@shared/utils/publicRpc';
import { portalSubmitInduction, portalSubmitBySubmissionId } from '@shared/services/portalService';
import SignatureCanvas from '@shared/components/SignatureCanvas';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Textarea } from '@shared/components/ui/textarea';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { supabase } from '@shared/supabaseClient';

const PortalInductions = () => {
  const { session, contextInduction } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { submissionId } = useParams();
  // Return to wherever we came from (project setup guide, invite setup guide, or the invites list).
  const returnTo = location.state?.returnTo || '/portal/inductions';
  const token = useInviteToken();
  const signatureRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [error, setError] = useState(null);
  const [showBriefContent, setShowBriefContent] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);


  // Multi-brief state
  const [briefList, setBriefList] = useState([]);
  const [selectedBriefIndex, setSelectedBriefIndex] = useState(0);
  const [briefStates, setBriefStates] = useState({}); // per-brief answers, signature, viewed, acknowledged

  // Computed: currently selected brief
  const brief = briefList[selectedBriefIndex] || null;
  const briefKey = brief?.submission_id || `brief-${selectedBriefIndex}`;
  const currentState = briefStates[briefKey] || {};
  const answers = currentState.answers || {};
  const signatureDataUrl = currentState.signatureDataUrl || null;
  const briefViewed = currentState.briefViewed || false;
  const briefAcknowledged = currentState.briefAcknowledged || false;
  const submittedInfo = currentState.submittedInfo || null;

  // Helper to update per-brief state
  const updateBriefState = useCallback((key, updates) => {
    setBriefStates(prev => ({
      ...prev,
      [key]: { ...prev[key], ...updates },
    }));
  }, []);

  // Initialize answers for a brief's content sections
  const initializeAnswers = useCallback((briefData) => {
    const initialAnswers = {};
    if (briefData.content && briefData.content.sections) {
      briefData.content.sections.forEach((section) => {
        if (section.type === 'info') return;
        const key = section.id;
        let defaultAnswer = '';
        if (section.type === 'checkbox') defaultAnswer = false;
        initialAnswers[key] = {
          question: section.title,
          answer: defaultAnswer,
          type: section.type,
          required: section.required || false,
        };
      });
    }
    return initialAnswers;
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function fetchBriefs() {
      try {
        setLoading(true);
        setError(null);

        const email = session?.person?.email || session?.email;

        // ── Route-based: load a specific submission by ID ────────────
        if (submissionId && email) {
          const { data, error: rpcError } = await supabase.rpc('portal_get_briefs_by_email', {
            p_email: email,
          });

          if (!isMounted) return;
          if (rpcError) { setError('Unable to load induction'); return; }

          // Find the specific submission
          const match = (data || []).find(row => row.submission_id === submissionId);
          if (!match) {
            setError('Induction not found.');
            return;
          }

          const singleBrief = {
            submission_id: match.submission_id,
            title: match.title,
            content: match.content,
            brief_type: match.brief_type || 'json',
            file_url: match.file_url,
            file_size_bytes: match.file_size_bytes,
            file_duration_seconds: match.file_duration_seconds,
            is_mandatory: match.is_mandatory !== false,
            status: match.status,
            depot_name: match.depot_name,
            public_reference: match.public_reference,
            brief_version: match.brief_version || null,
            auto_approved: match.auto_approved === true,
          };

          setBriefList([singleBrief]);
          setSelectedBriefIndex(0);

          const key = singleBrief.submission_id;
          if (!briefStates[key]) {
            let savedSubmittedInfo = null;
            try {
              const raw = localStorage.getItem(`portal_induction_submitted_${key}`);
              if (raw) savedSubmittedInfo = JSON.parse(raw);
            } catch { /* ignore */ }
            if (!savedSubmittedInfo && (singleBrief.status === 'submitted' || singleBrief.status === 'approved')) {
              savedSubmittedInfo = { reference: singleBrief.public_reference || null, submitted_at: null };
            }
            setBriefStates(prev => ({
              ...prev,
              [key]: {
                answers: initializeAnswers(singleBrief),
                signatureDataUrl: null,
                briefViewed: false,
                briefAcknowledged: false,
                submittedInfo: savedSubmittedInfo,
              },
            }));
          }
          return;
        }

        // ── Legacy: email-based multi-brief (used when deep-linked with token) ──
        if (email && !submissionId) {
          const { data, error: rpcError } = await supabase.rpc('portal_get_briefs_by_email', {
            p_email: email,
          });

          if (!isMounted) return;

          if (rpcError) {
            console.error('Error fetching briefs by email:', rpcError);
            setError('Unable to load inductions');
            return;
          }

          if (data && data.length > 0) {
    // Status priority: pending_acknowledgement ranks highest so it surfaces above
  // old 'approved' rows for the same brief in the deduplication.
  const STATUS_RANK = { pending_acknowledgement: 5, approved: 4, submitted: 3, in_progress: 2, invited: 1 };

            // Deduplicate by brief_id — keep the highest-status row per brief.
            // Guards against stale 'invited' rows existing alongside a 'submitted' row
            // for the same brief (can happen if DB dedup hasn't run yet).
            // IMPORTANT: fall back to submission_id if brief_id is missing (old RPC versions
            // didn't return brief_id — without this fallback all rows share the key
            // `undefined` and only the highest-status one survives, hiding pending briefs).
            const bestByBrief = new Map();
            data.forEach(row => {
              const key = row.brief_id || row.submission_id;
              const existing = bestByBrief.get(key);
              const currentRank = STATUS_RANK[row.status] ?? 0;
              const existingRank = STATUS_RANK[existing?.status] ?? 0;
              if (!existing || currentRank > existingRank) {
                bestByBrief.set(key, row);
              }
            });
            const dedupedData = Array.from(bestByBrief.values());

            // Map RPC result to a uniform brief shape
            const briefs = dedupedData.map(row => ({
              submission_id: row.submission_id,
              title: row.title,
              content: row.content,
              brief_type: row.brief_type || 'json',
              file_url: row.file_url,
              file_size_bytes: row.file_size_bytes,
              file_duration_seconds: row.file_duration_seconds,
              is_mandatory: row.is_mandatory !== false,
              status: row.status,
              depot_name: row.depot_name,
              public_reference: row.public_reference,
              brief_version: row.brief_version || null,
              auto_approved: row.auto_approved === true,
            }));

            setBriefList(briefs);

            // Initialize answers for all briefs that haven't been initialized yet
            const newStates = {};
            briefs.forEach((b, i) => {
              const key = b.submission_id || `brief-${i}`;
              if (!briefStates[key]) {
                // Restore submission info from localStorage (survives navigation away/back)
                let savedSubmittedInfo = null;
                if (b.submission_id) {
                  try {
                    const raw = localStorage.getItem(`portal_induction_submitted_${b.submission_id}`);
                    if (raw) savedSubmittedInfo = JSON.parse(raw);
                  } catch { /* ignore */ }
                }
                // Also treat DB-confirmed statuses as completed
                if (!savedSubmittedInfo && (b.status === 'submitted' || b.status === 'approved')) {
                  savedSubmittedInfo = { reference: b.public_reference || null, submitted_at: null };
                }
                newStates[key] = {
                  answers: initializeAnswers(b),
                  signatureDataUrl: null,
                  briefViewed: false,
                  briefAcknowledged: false,
                  submittedInfo: savedSubmittedInfo,
                };
              }
            });
            if (Object.keys(newStates).length > 0) {
              setBriefStates(prev => ({ ...prev, ...newStates }));
            }

            // Select first pending brief
            const firstPendingIdx = briefs.findIndex(b => b.status === 'invited' || b.status === 'in_progress' || b.status === 'pending_acknowledgement');
            if (firstPendingIdx >= 0) {
              setSelectedBriefIndex(firstPendingIdx);
            }
            return;
          }

          // No submissions found — if this user is a team member on a project,
          // auto-create the missing induction_submissions rows then re-fetch.
          const { data: ensureResult } = await supabase.rpc(
            'portal_ensure_team_member_inductions',
            { p_email: email }
          );

          if (ensureResult?.created) {
            // Submissions were just created — re-fetch
            const { data: retryData, error: retryError } = await supabase.rpc(
              'portal_get_briefs_by_email',
              { p_email: email }
            );

            if (!isMounted) return;

            if (!retryError && retryData && retryData.length > 0) {
              const STATUS_RANK = { pending_acknowledgement: 5, approved: 4, submitted: 3, in_progress: 2, invited: 1 };
              const bestByBrief = new Map();
              retryData.forEach(row => {
                const key = row.brief_id || row.submission_id;
                const existing = bestByBrief.get(key);
                const currentRank = STATUS_RANK[row.status] ?? 0;
                const existingRank = STATUS_RANK[existing?.status] ?? 0;
                if (!existing || currentRank > existingRank) bestByBrief.set(key, row);
              });
              const briefs = Array.from(bestByBrief.values()).map(row => ({
                submission_id: row.submission_id,
                title: row.title,
                content: row.content,
                brief_type: row.brief_type || 'json',
                file_url: row.file_url,
                file_size_bytes: row.file_size_bytes,
                file_duration_seconds: row.file_duration_seconds,
                is_mandatory: row.is_mandatory !== false,
                status: row.status,
                depot_name: row.depot_name,
                public_reference: row.public_reference,
                brief_version: row.brief_version || null,
                auto_approved: row.auto_approved === true,
              }));
              setBriefList(briefs);
              const newStates = {};
              briefs.forEach((b, i) => {
                const key = b.submission_id || `brief-${i}`;
                newStates[key] = {
                  answers: initializeAnswers(b),
                  signatureDataUrl: null,
                  briefViewed: false,
                  briefAcknowledged: false,
                  submittedInfo: (b.status === 'submitted' || b.status === 'approved')
                    ? { reference: b.public_reference || null, submitted_at: null }
                    : null,
                };
              });
              if (Object.keys(newStates).length > 0) {
                setBriefStates(prev => ({ ...prev, ...newStates }));
              }
              const firstPendingIdx = briefs.findIndex(b => b.status === 'invited' || b.status === 'in_progress' || b.status === 'pending_acknowledgement');
              if (firstPendingIdx >= 0) setSelectedBriefIndex(firstPendingIdx);
              return;
            }
          }
        }

        // Fallback: token-only flow (single brief, no email in session)
        if (token) {
          const data = await getBrief(token);
          if (!isMounted) return;

          if (data) {
            const singleBrief = {
              ...data,
              brief_type: data.brief_type || 'json',
              submission_id: null,
              status: 'invited',
            };
            setBriefList([singleBrief]);
            const key = 'brief-0';
            setBriefStates(prev => ({
              ...prev,
              [key]: {
                answers: initializeAnswers(data),
                signatureDataUrl: null,
                briefViewed: false,
                briefAcknowledged: false,
                submittedInfo: null,
              },
            }));
          }
        }
      } catch (err) {
        if (!isMounted) return;
        setError(parseRpcError(err));
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchBriefs();

    return () => { isMounted = false; };
  }, [submissionId, token, session?.person?.email || session?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnswerChange = (key, value) => {
    const newAnswers = {
      ...answers,
      [key]: { ...answers[key], answer: value },
    };
    updateBriefState(briefKey, { answers: newAnswers });
  };

  const handleSignatureChange = (dataUrl) => {
    updateBriefState(briefKey, { signatureDataUrl: dataUrl });
  };

  const handleBriefViewed = () => {
    updateBriefState(briefKey, { briefViewed: true });
  };

  const handleBriefAcknowledged = (checked) => {
    updateBriefState(briefKey, { briefAcknowledged: checked });
  };

  // ── Acknowledgement handler (pending_acknowledgement flow) ─────────────────
  const handleAcknowledge = async () => {
    if (!brief?.submission_id) {
      setError('Unable to acknowledge — submission ID missing.');
      return;
    }
    try {
      setAcknowledging(true);
      setError(null);

      const { data, error: rpcError } = await supabase.rpc('portal_acknowledge_induction', {
        p_submission_id: brief.submission_id,
        p_email: session?.person?.email || session?.email,
      });

      if (rpcError) throw rpcError;
      if (!data?.success) throw new Error('Acknowledgement failed — please try again.');

      const submittedPayload = { reference: brief.public_reference || data.reference, submitted_at: new Date().toISOString() };
      updateBriefState(briefKey, { submittedInfo: submittedPayload });
      window.dispatchEvent(new Event('induction-status-changed'));
      try {
        localStorage.setItem(`portal_induction_submitted_${brief.submission_id}`, JSON.stringify(submittedPayload));
      } catch { /* ignore */ }

      // Fire pass email — fire-and-forget (don't block navigation on email success)
      supabase.functions.invoke('send-induction-pass', {
        body: {
          pass_token:     data.pass_token,
          person_name:    data.person_name,
          person_email:   data.person_email,
          person_company: data.person_company || '',
          person_type:    data.person_type    || 'contractor',
          valid_from:     data.valid_from,
          valid_until:    data.valid_until,
          reference:      data.reference,
          qr_data:        data.pass_token,
          project_name:   data.project_name,
        },
      }).catch((emailErr) => {
        console.warn('Badge email send failed (non-fatal):', emailErr);
      });

      // Navigate directly to digital badge — no admin review needed for returning workers
      setTimeout(() => navigate(`/portal/digital-badge?submission=${brief.submission_id}`, { replace: true }), 400);
    } catch (err) {
      setError(err.message || 'Acknowledgement failed — please try again.');
    } finally {
      setAcknowledging(false);
    }
  };

  const validateForm = () => {
    if (!brief) return 'No brief selected.';

    // For PDF/video briefs, check viewing
    if (brief.brief_type === 'pdf' || brief.brief_type === 'video') {
      if (!briefViewed) return 'Please view the required brief document before submitting.';
    }

    // All brief types require the acknowledgement checkbox
    if (!briefAcknowledged) return 'Please confirm that you have read and understood the brief.';

    // For question-based briefs, check answers
    if (brief.brief_type === 'json' || brief.content?.sections?.length > 0) {
      for (const key in answers) {
        const answer = answers[key];
        if (!answer.required) continue;
        if (answer.type === 'checkbox' && answer.answer !== true) {
          return 'Please check all required boxes to proceed.';
        }
        if (answer.type !== 'checkbox' && !String(answer.answer || '').trim()) {
          return 'Please answer all required questions.';
        }
      }
    }

    if (!signatureDataUrl) return 'Please provide your signature.';
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Hard guard — should never be reachable via UI, but be defensive
    if (briefIsSubmitted) {
      setError('This induction has already been submitted. Please await the site team\'s review.');
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      let result;

      // Use submission_id flow for email-based multi-brief
      const submitEmail = session?.person?.email || session?.email;
      if (brief.submission_id && submitEmail) {
        result = await portalSubmitBySubmissionId(
          brief.submission_id,
          submitEmail,
          answers,
          signatureDataUrl
        );

        if (!result?.success) {
          throw new Error('Submission failed.');
        }

        const submittedPayload = {
          reference: result.reference,
          submitted_at: result.submitted_at,
        };
        updateBriefState(briefKey, { submittedInfo: submittedPayload });
        // Notify PortalLayout to refresh sidebar nav indicators immediately
        window.dispatchEvent(new Event('induction-status-changed'));
        // Persist so navigating away and back still shows completed state
        if (brief.submission_id) {
          try {
            localStorage.setItem(`portal_induction_submitted_${brief.submission_id}`, JSON.stringify(submittedPayload));
          } catch { /* ignore */ }
        }
        // Clear invite token — prevents token-only fallback from showing a stale 'invited' state
        try { localStorage.removeItem('invite_token'); } catch { /* ignore */ }

        // Fire-and-forget: if this is a project team member submission, notify
        // the lead once all members have completed their inductions.
        supabase.functions.invoke('notify-team-inducted', {
          body: { submission_id: brief.submission_id },
        }).catch(() => { /* non-critical */ });

        // Fire-and-forget: notify the requesting staff member that this
        // induction has been submitted and is ready for approval.
        supabase.functions.invoke('notify-submission-ready', {
          body: {
            submission_id: brief.submission_id,
            person_name: result.person_name,
            person_email: result.person_email,
            person_company: result.person_company,
            person_type: result.person_type,
            requesting_person: result.requesting_person,
            reference: result.reference,
          },
        }).catch(() => { /* non-critical */ });
      } else {
        // Fallback: token-based single brief submit
        result = await portalSubmitInduction(token, answers, signatureDataUrl);
        if (!result?.ok) {
          throw new Error(result?.error?.message || 'Submission failed.');
        }

        const tokenSubmittedPayload = {
          reference: result.reference,
          submitted_at: result.submitted_at,
          notification: result.notification,
        };
        updateBriefState(briefKey, { submittedInfo: tokenSubmittedPayload });
        // Notify PortalLayout to refresh sidebar nav indicators immediately
        window.dispatchEvent(new Event('induction-status-changed'));
        // Persist via reference key for token-based flow (no submission_id)
        if (result.reference) {
          try {
            localStorage.setItem(`portal_induction_submitted_ref_${result.reference}`, JSON.stringify(tokenSubmittedPayload));
          } catch { /* ignore */ }
        }
        // Clear invite token — prevents this brief re-appearing as 'invited' on next load
        try { localStorage.removeItem('invite_token'); } catch { /* ignore */ }
      }

      // Return to the inductions list after submission
      setTimeout(() => navigate(returnTo, { replace: true }), 600);
    } catch (err) {
      setError(parseRpcError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const renderSection = (section, sectionIndex) => {
    const key = section.id || `section-${sectionIndex}`;
    const answer = answers[key];

    if (section.type === 'info') {
      return (
        <SectionCard key={key} title={section.title} subtitle="Please read this section carefully.">
          {section.content && (
            <div className="border-l-4 border-blue-600 bg-muted/30 rounded-r-lg p-4 mt-3">
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-foreground whitespace-pre-wrap">{section.content}</div>
              </div>
            </div>
          )}
        </SectionCard>
      );
    }

    return (
      <SectionCard key={key} title={section.title}>
        {section.type === 'checkbox' && (
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id={key}
              checked={answer?.answer === true}
              onChange={(e) => handleAnswerChange(key, e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border"
            />
            <label htmlFor={key} className="text-sm text-foreground">
              {section.label || section.title}
              {section.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          </div>
        )}

        {section.type === 'radio' && (
          <div className="space-y-2">
            {section.options?.map((option, idx) => (
              <label key={idx} className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name={key}
                  value={option}
                  checked={answer?.answer === option}
                  onChange={(e) => handleAnswerChange(key, e.target.value)}
                  className="h-4 w-4"
                />
                {option}
              </label>
            ))}
          </div>
        )}

        {section.type === 'textarea' && (
          <Textarea
            value={answer?.answer || ''}
            onChange={(e) => handleAnswerChange(key, e.target.value)}
            rows={4}
            placeholder="Your answer"
          />
        )}

        {section.type === 'text' && (
          <Input
            value={answer?.answer || ''}
            onChange={(e) => handleAnswerChange(key, e.target.value)}
            placeholder="Your answer"
          />
        )}

        {section.type === 'select' && (
          <select
            value={answer?.answer || ''}
            onChange={(e) => handleAnswerChange(key, e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">Select an option...</option>
            {section.options?.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        )}
      </SectionCard>
    );
  };

  // Determine if the selected brief is already submitted/completed
  const briefIsSubmitted = submittedInfo || brief?.status === 'submitted' || brief?.status === 'approved';
  // Pending acknowledgement: returning worker who must confirm in portal
  const isPendingAck = !briefIsSubmitted && brief?.status === 'pending_acknowledgement';

  return (
    <div className="space-y-6">
      <InviteTabBar submissionId={submissionId} inviteStatus={contextInduction?.status} />
      <PageHeader
        title={brief?.title || 'Induction'}
        subtitle={brief?.depot_name ? `${brief.depot_name}${brief.brief_version ? ` · v${brief.brief_version}` : ''}` : 'Complete this induction before your first site visit.'}
      />

      {/* No briefs found */}
      {!loading && briefList.length === 0 && !error && (
        <EmptyState
          icon={ClipboardList}
          title="No induction assigned yet"
          description="The site team will send you an email invitation when an induction is ready for you to complete. Nothing to do here yet."
        />
      )}

      {loading && (
        <div className="text-center text-sm text-muted-foreground">Loading induction briefs...</div>
      )}

      {error && !brief && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Already-submitted brief view */}
      {brief && briefIsSubmitted && (
        <SectionCard>
          {/* Heading */}
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="h-8 w-8 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">
                {brief.status === 'approved' ? 'Induction Approved' : 'Induction Submitted'}
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                {brief.title}
                {submittedInfo?.reference && <> &bull; Ref: <span className="font-semibold text-foreground">{submittedInfo.reference}</span></>}
              </p>
            </div>
          </div>

          {/* Status message */}
          <div className="border-l-4 border-emerald-600 bg-muted/40 rounded-r-lg p-4 mb-5">
            <p className="text-sm font-medium text-foreground">
              {brief.auto_approved
                ? 'This induction has been automatically approved — the document has not changed since your last visit. No action required.'
                : brief.status === 'approved'
                  ? 'Your induction has been approved by the site team. Your digital site pass is ready.'
                  : 'Your responses have been submitted and the site team has been notified. You\'ll receive an email once reviewed — this is usually within one working day.'}
            </p>
            {brief.brief_version && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Document version: <span className="font-mono font-semibold">v{brief.brief_version}</span>
              </p>
            )}
          </div>

          {/* Refresh memory option */}
          <div>
            <p className="text-sm font-semibold text-foreground mb-3">Need to refresh your memory?</p>
            {brief.file_url ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => window.open(brief.file_url, '_blank')}
              >
                {brief.brief_type === 'video'
                  ? <><FileVideo className="h-4 w-4 mr-2" />View Induction Video</>
                  : <><FileText className="h-4 w-4 mr-2" />View Induction Document</>}
              </Button>
            ) : brief.content?.sections?.length > 0 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setReviewOpen(rv => !rv)}
              >
                <Eye className="h-4 w-4 mr-2" />
                {reviewOpen ? 'Hide Induction Brief' : 'View Induction Brief'}
              </Button>
            ) : null}
          </div>

          {/* Read-only brief content */}
          {reviewOpen && brief.content?.sections && (
            <div className="mt-4 space-y-3 border-t border-border pt-4">
              {brief.content.description && (
                <div className="border-l-4 border-amber-500 bg-muted/30 rounded-r-lg p-4">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{brief.content.description}</p>
                </div>
              )}
              {brief.content.sections.filter(s => s.type === 'info').map((section, i) => (
                <div key={i} className="border-l-4 border-blue-600 bg-muted/30 rounded-r-lg p-4">
                  <p className="text-sm font-semibold text-foreground mb-1">{section.title}</p>
                  {section.content && <p className="text-sm text-foreground whitespace-pre-wrap">{section.content}</p>}
                </div>
              ))}
              <p className="text-xs text-muted-foreground italic">This is a read-only view. Your induction has already been submitted.</p>
            </div>
          )}

        </SectionCard>
      )}

      {/* Pending acknowledgement — returning worker must review + confirm */}
      {brief && isPendingAck && (
        <SectionCard>
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">Re-confirm Your Site Induction</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                {brief.title}
                {brief.brief_version && (
                  <> &bull; <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">v{brief.brief_version}</span></>
                )}
              </p>
            </div>
          </div>

          <div className="border-l-4 border-blue-600 bg-muted/30 rounded-r-lg p-4 mb-5">
            <p className="text-sm font-medium text-foreground">
              You have previously completed this induction — you do not need to fill in any forms again.
              Please take a moment to review the brief below, then click confirm.
              Your acknowledgement will be sent for team leader review before your pass is issued.
            </p>
          </div>

          {/* Read-only brief content */}
          {(brief.file_url) ? (
            <div className="mb-5">
              <p className="text-sm font-semibold text-foreground mb-3">Induction Brief</p>
              <Button
                type="button"
                variant="outline"
                onClick={() => window.open(brief.file_url, '_blank')}
              >
                {brief.brief_type === 'video'
                  ? <><FileVideo className="h-4 w-4 mr-2" />View Induction Video</>
                  : <><FileText className="h-4 w-4 mr-2" />View Induction Document</>}
              </Button>
            </div>
          ) : brief.content?.sections?.length > 0 ? (
            <div className="mb-5 space-y-3">
              <p className="text-sm font-semibold text-foreground">Induction Brief Content</p>
              {brief.content.description && (
                <div className="border-l-4 border-amber-500 bg-muted/30 rounded-r-lg p-4">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{brief.content.description}</p>
                </div>
              )}
              {brief.content.sections.filter(s => s.type === 'info').map((section, i) => (
                <div key={i} className="border-l-4 border-blue-600 bg-muted/30 rounded-r-lg p-4">
                  <p className="text-sm font-semibold text-foreground mb-1">{section.title}</p>
                  {section.content && <p className="text-sm text-foreground whitespace-pre-wrap">{section.content}</p>}
                </div>
              ))}
              {brief.content.sections.filter(s => s.type !== 'info').length > 0 && (
                <p className="text-xs text-muted-foreground italic">
                  This is a read-only review. You do not need to re-answer the questions.
                </p>
              )}
            </div>
          ) : null}

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <p className="text-sm text-muted-foreground">
              By confirming, you acknowledge that you have read and understood the current induction brief.
            </p>
            <Button
              type="button"
              onClick={handleAcknowledge}
              disabled={acknowledging}
              className="ml-4 shrink-0"
            >
              {acknowledging ? 'Confirming…' : 'Confirm I have reviewed this induction'}
            </Button>
          </div>
        </SectionCard>
      )}

      {/* Active brief form */}
      {brief && !briefIsSubmitted && !isPendingAck && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ============================================================ */}
          {/* PDF/VIDEO BRIEF */}
          {/* ============================================================ */}
          {(brief.brief_type === 'pdf' || brief.brief_type === 'video') && (
            <>
              <SectionCard title="Required Brief" subtitle="You must review this brief before completing your induction.">
                <div className="space-y-4">
                  <div className="border border-border bg-card rounded-lg p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 mt-1 p-2.5 rounded-lg bg-primary/10">
                        {brief.brief_type === 'video' ? (
                          <FileVideo className="h-6 w-6 text-primary" />
                        ) : (
                          <FileText className="h-6 w-6 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        <div>
                          <h3 className="text-lg font-semibold text-foreground">{brief.title}</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {brief.brief_type === 'video' ? 'Video Brief' : 'PDF Document'}
                            {brief.file_size_bytes && (
                              <> &bull; {(brief.file_size_bytes / 1024 / 1024).toFixed(2)} MB</>
                            )}
                            {brief.brief_type === 'video' && brief.file_duration_seconds && (
                              <> &bull; {Math.floor(brief.file_duration_seconds / 60)}:{(brief.file_duration_seconds % 60).toString().padStart(2, '0')} min</>
                            )}
                          </p>
                        </div>

                        {brief.content?.description && (
                          <div className="border border-border rounded-md p-3 bg-muted/20">
                            <p className="text-sm text-foreground whitespace-pre-wrap">{brief.content.description}</p>
                          </div>
                        )}

                        <div className="flex gap-3">
                          <Button
                            type="button"
                            onClick={() => {
                              window.open(brief.file_url, '_blank');
                              handleBriefViewed();
                            }}
                            className="bg-primary hover:bg-primary/90"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            {briefViewed ? 'View Again' : 'View Brief'}
                          </Button>
                          {briefViewed && (
                            <div className="flex items-center gap-2 text-sm text-emerald-600 font-medium">
                              <CheckCircle2 className="h-4 w-4" />
                              <span>Brief viewed</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {briefViewed && (
                    <div className="border border-border rounded-lg p-4 bg-muted/20">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id={`ack-${briefKey}`}
                          checked={briefAcknowledged}
                          onChange={(e) => handleBriefAcknowledged(e.target.checked)}
                          className="mt-1 h-5 w-5 rounded border-border"
                        />
                        <label htmlFor={`ack-${briefKey}`} className="text-sm text-foreground font-medium">
                          I confirm that I have read and understood the induction brief &ldquo;{brief.title}&rdquo;.
                          <span className="text-red-600 ml-1">*</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {!briefViewed && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        You must view and read the brief document before you can complete your induction.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Signature is part of this flow — shown once the brief is acknowledged */}
                  {briefViewed && briefAcknowledged && (
                    <div className="border-t border-border pt-5 mt-2 space-y-4">
                      <div className="flex items-center gap-3 p-3 rounded-r-lg border-l-4 border-emerald-600 bg-muted/30">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                        <p className="text-sm font-medium text-foreground">
                          Brief confirmed — one last step. Sign below to complete your submission.
                        </p>
                      </div>
                      <SignatureCanvas
                        ref={signatureRef}
                        label="Your signature"
                        hint="Sign above to certify your submission."
                        onSignatureChange={handleSignatureChange}
                      />
                      <div className="flex justify-end">
                        <Button type="button" variant="ghost" size="sm" onClick={() => signatureRef.current?.clear()}>
                          Clear signature
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Nudge: brief viewed but not yet confirmed */}
                  {briefViewed && !briefAcknowledged && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border">
                      <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <p className="text-sm font-medium text-muted-foreground">
                        Tick the confirmation box above to unlock your signature and submit.
                      </p>
                    </div>
                  )}
                </div>
              </SectionCard>
            </>
          )}

          {/* ============================================================ */}
          {/* QUESTION-BASED BRIEF */}
          {/* ============================================================ */}
          {(brief.brief_type === 'json' || (!brief.brief_type && brief.content?.sections)) && (
            <>
              <SectionCard title={brief.title} subtitle="Please read the brief content and complete all required sections.">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Read each section carefully and answer every required question.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBriefContent(!showBriefContent)}
                  >
                    {showBriefContent ? (
                      <><ChevronUp className="h-4 w-4 mr-2" />Hide Brief Content</>
                    ) : (
                      <><ChevronDown className="h-4 w-4 mr-2" />Show Brief Content</>
                    )}
                  </Button>
                </div>
              </SectionCard>

              {showBriefContent && brief.content?.description && (
                <SectionCard title="Brief Description" subtitle="Overview of this induction">
                  <div className="border-l-4 border-amber-500 bg-muted/30 rounded-r-lg p-4">
                    <div className="flex items-start gap-3">
                      <FileText className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-foreground whitespace-pre-wrap">{brief.content.description}</p>
                    </div>
                  </div>
                </SectionCard>
              )}

              {showBriefContent && brief.file_url && (
                <SectionCard title="Brief Document" subtitle="View or download the induction document">
                  <div className="border border-border rounded-lg p-4 bg-card">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-md bg-primary/10">
                        {brief.brief_type === 'video' ? (
                          <FileVideo className="h-5 w-5 text-primary" />
                        ) : (
                          <FileText className="h-5 w-5 text-primary" />
                        )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {brief.brief_type === 'video' ? 'Video Brief' : 'PDF Document'}
                          </p>
                          {brief.file_size_bytes && (
                            <p className="text-xs text-muted-foreground">
                              Size: {(brief.file_size_bytes / 1024 / 1024).toFixed(2)} MB
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => { window.open(brief.file_url, '_blank'); handleBriefViewed(); }}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          {briefViewed ? 'Open Again' : 'Open Document'}
                        </Button>
                        {briefViewed && (
                          <div className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium">
                            <CheckCircle2 className="h-4 w-4" />
                            Viewed
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </SectionCard>
              )}

              {showBriefContent && brief.content?.sections
                ?.filter(section => section.type === 'info')
                .map((section, index) => renderSection(section, index))}

              {showBriefContent && brief.content?.sections?.some(s => s.type === 'info') && (
                <div className="border-t border-border pt-6">
                  <h3 className="text-lg font-semibold text-foreground mb-2">Complete Induction Questions</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    Now that you&apos;ve reviewed the brief, please answer the following questions.
                  </p>
                </div>
              )}

              {brief.content?.sections
                ?.filter(section => section.type !== 'info')
                .map((section, index) => renderSection(section, index))}
            </>
          )}

          {/* ============================================================ */}
          {/* CONFIRMATION & SIGNATURE — question briefs only; PDF/video embeds it above */}
          {/* ============================================================ */}
          {(brief.brief_type === 'json' || (!brief.brief_type && brief.content?.sections)) && (
            <SectionCard title="Signature" subtitle="Confirm you have read and understood the induction.">
              {/* Must view document first if one exists */}
              {brief.file_url && !briefViewed && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border mb-4">
                  <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm font-medium text-foreground">
                    You must open and read the brief document above before you can confirm and submit.
                  </p>
                </div>
              )}
              {/* Confirmation checkbox — gated on briefViewed when doc exists */}
              {(!brief.file_url || briefViewed) && (
              <div className="border border-border rounded-lg p-4 mb-4 bg-muted/20">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id={`ack-json-${briefKey}`}
                    checked={briefAcknowledged}
                    onChange={(e) => handleBriefAcknowledged(e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-border"
                  />
                  <label htmlFor={`ack-json-${briefKey}`} className="text-sm text-foreground font-medium">
                    I confirm that I have read and understood the induction brief &ldquo;{brief.title}&rdquo;.
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                </div>
              </div>
              )}

              {/* Signature — unlocked once checkbox is ticked */}
              {briefAcknowledged ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-r-lg border-l-4 border-emerald-600 bg-muted/30">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                    <p className="text-sm font-medium text-foreground">
                      Brief confirmed — one last step. Sign below to complete your submission.
                    </p>
                  </div>
                  <SignatureCanvas
                    ref={signatureRef}
                    label="Your signature"
                    hint="Sign above to certify your submission."
                    onSignatureChange={handleSignatureChange}
                  />
                  <div className="flex justify-end">
                    <Button type="button" variant="ghost" size="sm" onClick={() => signatureRef.current?.clear()}>
                      Clear signature
                    </Button>
                  </div>
                </div>
              ) : (
                (!brief.file_url || briefViewed) && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border">
                  <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm font-medium text-muted-foreground">
                    Tick the confirmation box above to unlock your signature and submit.
                  </p>
                </div>
                )
              )}
            </SectionCard>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={submitting || !briefAcknowledged || (brief?.file_url && !briefViewed)}
            >
              {submitting ? 'Submitting...' : 'Submit Induction'}
            </Button>
          </div>
        </form>
      )}

    </div>
  );
};

export default PortalInductions;
